/**
 * useUnifiedPlacement Hook
 * Manages placement state and recent items for the Unified Placement Palette
 */

import { useState, useCallback, useEffect } from 'react';
import { logger } from '@/lib/logger';

// Types
export type PlacementItemType = 'fixture' | 'control' | 'product';

export interface PlacementItem {
  type: PlacementItemType;
  category: string;
  subcategory?: string;
  productLibraryId?: string;
  displayName: string;
  iconSvg?: string;
  iconUrl?: string;
  iconColor?: string;
}

export interface RecentItem extends PlacementItem {
  lastUsed: number;
  useCount: number;
}

export interface UnifiedPlacementState {
  // Palette state
  isPaletteOpen: boolean;
  paletteFilter: 'all' | 'fixture' | 'control' | 'product' | null;

  // Current placement
  currentItem: PlacementItem | null;

  // Session tracking
  placementCount: number;

  // Recents
  recentItems: RecentItem[];
}

const STORAGE_KEY = 'vfloow_recent_placements';
const MAX_RECENT_ITEMS = 10;

// Helper to load recents from localStorage
function loadRecents(): RecentItem[] {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored) {
      const parsed = JSON.parse(stored);
      if (Array.isArray(parsed)) {
        return parsed;
      }
    }
  } catch (error) {
    logger.error('[UnifiedPlacement] Error loading recents:', error);
  }
  return [];
}

// Helper to save recents to localStorage
function saveRecents(items: RecentItem[]): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(items));
  } catch (error) {
    logger.error('[UnifiedPlacement] Error saving recents:', error);
  }
}

// Helper to generate a unique key for an item
function getItemKey(item: PlacementItem): string {
  if (item.productLibraryId) {
    return `product:${item.productLibraryId}`;
  }
  return `${item.type}:${item.category}:${item.subcategory || ''}`;
}

// Event name for placement tracking
export const PLACEMENT_EVENT = 'vfloow:item-placed';

// Dispatch a placement event (call this from FloorPlanViewport when items are created)
export function dispatchPlacementEvent(
  type: PlacementItemType,
  category: string,
  subcategory?: string,
) {
  window.dispatchEvent(
    new CustomEvent(PLACEMENT_EVENT, {
      detail: { type, category, subcategory },
    }),
  );
}

export function useUnifiedPlacement() {
  // State
  const [isPaletteOpen, setIsPaletteOpen] = useState(false);
  const [paletteFilter, setPaletteFilter] = useState<
    'all' | 'fixture' | 'control' | 'product' | null
  >(null);
  const [currentItem, setCurrentItem] = useState<PlacementItem | null>(null);
  const [placementCount, setPlacementCount] = useState(0);
  const [recentItems, setRecentItems] = useState<RecentItem[]>(() => loadRecents());

  // Persist recents to localStorage when they change
  useEffect(() => {
    saveRecents(recentItems);
  }, [recentItems]);

  // Listen for placement events from FloorPlanViewport
  useEffect(() => {
    const handlePlacement = (event: CustomEvent) => {
      const { type, category, subcategory } = event.detail;

      // Only process if we're in placement mode for this type
      if (currentItem && currentItem.type === type) {
        // Increment session counter
        setPlacementCount(prev => prev + 1);

        // Also update recents with the current item info
        setRecentItems(prev => {
          const key = getItemKey(currentItem);
          const existingIndex = prev.findIndex(r => getItemKey(r) === key);

          if (existingIndex >= 0) {
            // Update existing item
            const updated = [...prev];
            updated[existingIndex] = {
              ...updated[existingIndex],
              lastUsed: Date.now(),
              useCount: updated[existingIndex].useCount + 1,
            };
            // Sort by last used
            updated.sort((a, b) => b.lastUsed - a.lastUsed);
            return updated;
          } else {
            // Add new item
            const newItem: RecentItem = {
              ...currentItem,
              lastUsed: Date.now(),
              useCount: 1,
            };
            const updated = [newItem, ...prev].slice(0, MAX_RECENT_ITEMS);
            return updated;
          }
        });

        logger.log('[UnifiedPlacement] Placement recorded:', currentItem.displayName);
      }
    };

    window.addEventListener(PLACEMENT_EVENT, handlePlacement as EventListener);
    return () => {
      window.removeEventListener(PLACEMENT_EVENT, handlePlacement as EventListener);
    };
  }, [currentItem]);

  // Open the palette
  const openPalette = useCallback((filter?: 'all' | 'fixture' | 'control' | 'product') => {
    setIsPaletteOpen(true);
    setPaletteFilter(filter || 'all');
    logger.log('[UnifiedPlacement] Palette opened with filter:', filter || 'all');
  }, []);

  // Close the palette
  const closePalette = useCallback(() => {
    setIsPaletteOpen(false);
    setPaletteFilter(null);
    logger.log('[UnifiedPlacement] Palette closed');
  }, []);

  // Toggle the palette
  const togglePalette = useCallback(() => {
    if (isPaletteOpen) {
      closePalette();
    } else {
      openPalette('all');
    }
  }, [isPaletteOpen, openPalette, closePalette]);

  // Select an item for placement
  const selectItem = useCallback(
    (item: PlacementItem) => {
      setCurrentItem(item);
      setPlacementCount(0);
      closePalette();
      logger.log('[UnifiedPlacement] Item selected for placement:', item.displayName);
    },
    [closePalette],
  );

  // Clear the current placement
  const clearPlacement = useCallback(() => {
    setCurrentItem(null);
    setPlacementCount(0);
    logger.log('[UnifiedPlacement] Placement cleared');
  }, []);

  // Record a placement (called when user places item on floor plan)
  const recordPlacement = useCallback(
    (item?: PlacementItem) => {
      const placedItem = item || currentItem;
      if (!placedItem) return;

      // Increment session counter
      setPlacementCount(prev => prev + 1);

      // Update recents
      setRecentItems(prev => {
        const key = getItemKey(placedItem);
        const existingIndex = prev.findIndex(r => getItemKey(r) === key);

        if (existingIndex >= 0) {
          // Update existing item
          const updated = [...prev];
          updated[existingIndex] = {
            ...updated[existingIndex],
            lastUsed: Date.now(),
            useCount: updated[existingIndex].useCount + 1,
          };
          // Sort by last used
          updated.sort((a, b) => b.lastUsed - a.lastUsed);
          return updated;
        } else {
          // Add new item
          const newItem: RecentItem = {
            ...placedItem,
            lastUsed: Date.now(),
            useCount: 1,
          };
          const updated = [newItem, ...prev].slice(0, MAX_RECENT_ITEMS);
          return updated;
        }
      });

      logger.log('[UnifiedPlacement] Placement recorded:', placedItem.displayName);
    },
    [currentItem],
  );

  // Select a recent item
  const selectRecent = useCallback(
    (recent: RecentItem) => {
      const item: PlacementItem = {
        type: recent.type,
        category: recent.category,
        subcategory: recent.subcategory,
        productLibraryId: recent.productLibraryId,
        displayName: recent.displayName,
        iconSvg: recent.iconSvg,
        iconUrl: recent.iconUrl,
        iconColor: recent.iconColor,
      };
      selectItem(item);
    },
    [selectItem],
  );

  // Clear all recents
  const clearRecents = useCallback(() => {
    setRecentItems([]);
    logger.log('[UnifiedPlacement] Recents cleared');
  }, []);

  return {
    // Palette state
    isPaletteOpen,
    paletteFilter,
    openPalette,
    closePalette,
    togglePalette,
    setPaletteFilter,

    // Placement state
    currentItem,
    placementCount,
    selectItem,
    clearPlacement,
    recordPlacement,

    // Recents
    recentItems,
    selectRecent,
    clearRecents,
  };
}

export default useUnifiedPlacement;
